import React from "react";
import { motion } from "framer-motion";
import { FiCheckCircle } from "react-icons/fi";
import SectionHeading from "../SectionHeading";
import CertificationLogos from "../CertificationLogos";

const cardVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: "easeOut" } },
};

/**
 * The "our services" grid on a service detail page: one card per standard,
 * each led by that standard's real certification mark from the shared
 * registry, then its headline, paragraphs, a scope list on ticks and its
 * benefits on a tinted panel.
 *
 * The two lists are kept visually distinct so a card carrying both still
 * scans, and `mt-auto` on the benefits panel lines the panels up across a row
 * whatever each scope list runs to.
 *
 * Every label is passed in, never assumed: the approved documents introduce
 * each list with different wording per standard ("Our GOTS Consultancy
 * Includes:", "Our RCS Support:", "Our Sedex / SMETA Support:" …) and those
 * are not interchangeable.
 *
 * @param {string} id        heading id, for the section's aria-labelledby
 * @param {string} title     section heading
 * @param {array}  standards `{ id, logoKey, title, headline, paragraphs,
 *                            listLabel, items, benefitsLabel, benefits }`,
 *                            optionally `{ extraLabel, extraItems }` for a
 *                            standard whose document carries a third list
 *                            (e.g. "Industries Using FSC CoC:") — omitted by
 *                            every other standard, which renders nothing
 * @param {string} [tone]    "muted" (default, gray-50) or "light" (white)
 */
const ServiceStandardsSection = ({ id, title, standards, tone = "muted" }) => (
  <section
    className={`${tone === "light" ? "bg-white" : "bg-gray-50"} py-14 lg:py-20`}
    aria-labelledby={id}
  >
    <div className="brand-col">
      <SectionHeading id={id} title={title} />
      <motion.ul
        className="grid list-none grid-cols-1 gap-6 p-0 lg:grid-cols-2 lg:gap-8"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.05 }}
        variants={{ visible: { transition: { staggerChildren: 0.07 } } }}
      >
        {standards.map((standard) => (
          <motion.li
            key={standard.id}
            id={standard.id}
            variants={cardVariants}
            className="brand-card scroll-mt-24 p-6 lg:p-8"
          >
            <CertificationLogos
              logoKeys={[standard.logoKey]}
              label={`${standard.title} certification mark`}
              className="flex h-[4.5rem] items-center"
              size="h-[4.5rem]"
            />

            <h3 className="mt-4 text-lg lg:text-xl font-bold text-gray-900">
              {standard.title}
            </h3>
            <p className="mt-1 text-sm md:text-base font-semibold text-[var(--brand-primary)]">
              {standard.headline}
            </p>

            <div className="mt-4 space-y-3">
              {standard.paragraphs.map((paragraph) => (
                <p
                  key={paragraph}
                  className="text-sm md:text-base leading-relaxed text-gray-600"
                >
                  {paragraph}
                </p>
              ))}
            </div>

            <p className="mt-5 text-sm font-semibold text-gray-900">
              {standard.listLabel}
            </p>
            <ul className="mt-3 grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
              {standard.items.map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <FiCheckCircle
                    aria-hidden="true"
                    size={18}
                    className="mt-0.5 flex-shrink-0 text-[var(--brand-primary)]"
                  />
                  <span className="text-sm md:text-base text-gray-700">
                    {item}
                  </span>
                </li>
              ))}
            </ul>

            {standard.extraItems && (
              <>
                <p className="mt-5 text-sm font-semibold text-gray-900">
                  {standard.extraLabel}
                </p>
                <ul className="mt-3 flex flex-wrap gap-2">
                  {standard.extraItems.map((item) => (
                    <li
                      key={item}
                      className="rounded-full border border-gray-100 bg-gray-50 px-3 py-1.5 text-xs md:text-sm text-gray-700"
                    >
                      {item}
                    </li>
                  ))}
                </ul>
              </>
            )}

            <div className="mt-auto pt-6">
              <div className="rounded-xl bg-[rgba(104,46,146,0.05)] p-4 sm:p-5">
                <p className="text-sm font-semibold text-gray-900">
                  {standard.benefitsLabel}
                </p>
                <ul className="mt-3 space-y-2">
                  {standard.benefits.map((benefit) => (
                    <li key={benefit} className="flex items-start gap-3">
                      <span
                        aria-hidden="true"
                        className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[var(--brand-orange)]"
                      />
                      <span className="text-sm md:text-base text-gray-700">
                        {benefit}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.li>
        ))}
      </motion.ul>
    </div>
  </section>
);

export default ServiceStandardsSection;
